import { Form, Formik } from 'formik';
import { useCallback, useMemo, useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import TextBox from "../../../../controls/form/TextBox";
import TextArea from "../../../../controls/form/TextArea";
import Checkbox from "../../../../controls/form/Checkbox";
import Dropdown from "../../../../controls/form/Dropdown";
import Forms from "../../../../../services/forms";
import { useFormsConfig } from '../../Context';
import GenericCheckBox from '../../../../controls/GenericCheckbox';
import * as Yup from "yup";
import HeaderTabs from "../../controls/HeaderTabs";
import DragList from "../../controls/DragList";
import ActionButton from '../../../../settings/ActionButton';
import Label from '../../../../controls/form/Label';

const nameRegex = /^[a-zA-Z0-9_]+[a-zA-Z0-9]$/;

const EditFormTemplate = ({
    formTemplateId,
    show,
    allowUpdate,
    onUpdate,
    onClose
}) => {
    const { id: deploymentId } = useParams();
    const [translate] = useTranslation();
    const { formTemplate, dataTypes, logAmplitudeEvent, modalContainerRef, LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS } = useFormsConfig();

    const [showSaveButton, setShowSaveButton] = useState(false);
    const [triggerReorder, setTriggerReorder] = useState(false);
    const [sections, setSections] = useState(formTemplate?.sections || []);
    const [selectedTab, setSelectedTab] = useState("template");

    const configList = useMemo(() => {
        return [
            { _id: "template", displayValue: translate('OTM_TEMPLATE') },
            { _id: "sections", displayValue: translate('OTM_SECTIONS') },
            { _id: "preferences", displayValue: translate('OTM_FORMS_TEMPLATE_PREFERENCES') }
        ];
    }, [translate]);

    useEffect(() => {
        if (show) {
            setSections([...formTemplate.sections]);
        }
    }, [show, formTemplate?.sections]);

    const handleSubmit = useCallback(async (values) => {
        await Forms.updateTemplate({
            deploymentId,
            formTemplateId,
            body: values
        });

        if (triggerReorder) {
            await Forms.reorderSections({
                deploymentId,
                formTemplateId,
                body: sections.map(section => section._id)
            });
            setTriggerReorder(false);
        }

        logAmplitudeEvent({
            eventType: "updatedTemplate",
            eventProperties: {
                deploymentId,
                formTemplateId,
                formTemplateName: values.name
            }
        });
        setShowSaveButton(false);
        onUpdate();
    }, [deploymentId, formTemplateId, sections, triggerReorder, onUpdate, logAmplitudeEvent]);

    const getErrorList = useCallback((props) => {
        const errors = {};
        for (const fieldName in props.errors || {})
            switch (fieldName) {
                default:
                    errors.template = true;
                    break;
            }

        return errors;
    }, []);

    const renderHeader = useCallback((props) => {
        return <div className="row flex-grow-1 mt-n2 align-items-center justify-content-end">
            <div className="col-12 col-lg mt-2">
                <label className="h5">{translate("OTM_EDIT_FORM_TEMPLATE")}</label>
            </div>
            <HeaderTabs
                listData={configList}
                listAttr="displayValue"
                selectedTab={selectedTab}
                listOnClick={(_id) => setSelectedTab(_id)}
                errors={getErrorList(props)}
            />
        </div>
    }, [configList, selectedTab, translate, getErrorList]);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    const renderSections = useMemo(() => {
        return <DragList
            listTitle={translate('OTM_SECTIONS_ORDER')}
            emptyText={translate('OTM_NO_SECTIONS')}
            listData={sections}
            disabled={!allowUpdate}
            drag={{
                type: 'ComponentListItem',
                onReorder: (dragIndex, hoverIndex) => {
                    setTriggerReorder(true);
                    setShowSaveButton(true);
                    setSections(prev => {
                        if (dragIndex < hoverIndex) {
                            const auxSection = prev[dragIndex];
                            for (var i = dragIndex; i < hoverIndex; i++) {
                                prev[i] = prev[i + 1];
                            }
                            prev[hoverIndex] = auxSection;
                        }
                        else {
                            const auxSection = prev[dragIndex];
                            for (var j = dragIndex; j > hoverIndex; j--) {
                                prev[j] = prev[j - 1];
                            }
                            prev[hoverIndex] = auxSection;
                        }

                        return [...prev];
                    });
                }
            }}
        />
    }, [sections, allowUpdate, translate]);

    const templatePreferences = useCallback((props) => {
        const dataTypesOptions = dataTypes ? [{ _id: 'no-input', name: 'label-only' }, ...dataTypes.map(o => ({ _id: o, name: o }))] : [{ _id: 'no-input', name: 'label-only' }];

        return (
            <div className="row mt-n3 align-items-start">
                <div className='col-12 mt-3'>
                    <span className='h4'>{translate('OTM_FORMS_TEMPLATE_PREFERENCES_SECTION_HEADER')}</span>
                </div>
                <div className='col-12 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_SECTION_TITLE_EQUALS_NAME')}
                        name='preferences.sectionTitleEqualsName'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-12 mt-3'>
                    <span className='h4'>{translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_HEADER')}</span>
                </div>
                <div className='col-6 mt-3'>
                    <Dropdown
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_DATA_TYPE')}
                        name='preferences.itemDataType'
                        readOnly={!allowUpdate}
                        options={dataTypesOptions}
                        onSelect={handleOnChange}
                        grow={true}
                        addEmptyOption={true}
                    />
                </div>
                <div className='col-6 mt-3'>
                    <TextBox
                        label={translate("OTM_MAX_LENGTH")}
                        type="number"
                        name="preferences.itemMaxLength"
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-6 mt-3'>
                    <Dropdown
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_ALLOWED_DECIMALS')}
                        name='preferences.itemNumberAllowedDecimals'
                        readOnly={!allowUpdate}
                        options={DECIMALS_OPTIONS}
                        onSelect={handleOnChange}
                        grow={true}
                        addEmptyOption={true}
                    />
                </div>
                <div className='col-6 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_SHOW_STEP_BUTTONS')}
                        name='preferences.itemNumberShowStepButtons'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Dropdown
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_COLUMNS_SPAN_SMALL')}
                        name='preferences.itemColumnSpanSmall'
                        tooltip={translate('OTM_COLUMNS_SPAN_SMALL_TOOLTIP')}
                        buttonTooltip={true}
                        grow={true}
                        readOnly={!allowUpdate}
                        onSelect={handleOnChange}
                        options={COLUMNS_OPTIONS}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Dropdown
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_COLUMNS_SPAN_LARGE')}
                        name='preferences.itemColumnSpanLarge'
                        tooltip={translate('OTM_COLUMNS_SPAN_LARGE_TOOLTIP')}
                        buttonTooltip={true}
                        grow={true}
                        readOnly={!allowUpdate}
                        onSelect={handleOnChange}
                        options={COLUMNS_OPTIONS}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_REQUIRED')}
                        name='preferences.itemIsRequired'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_READ_ONLY')}
                        name='preferences.itemIsReadOnly'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_STYLES_WHEN_VALID')}
                        name='preferences.itemShowStylesWhenValid'
                        tooltip={translate('OTM_SHOW_STYLES_WHEN_VALID_TOOLTIP')}
                        buttonTooltip={true}
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-12 col-sm-6 mt-3'>
                    <Checkbox
                        label={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_STYLES_WHEN_INVALID')}
                        name='preferences.itemShowStylesWhenInvalid'
                        tooltip={translate('OTM_SHOW_STYLES_WHEN_INVALID_TOOLTIP')}
                        buttonTooltip={true}
                        disabled={!allowUpdate}
                    />
                </div>

                <div className='col-6 mt-3'>
                    <span className="d-block h6 lh-sm mb-0">
                        {translate("OTM_FORMS_TEMPLATE_PREFERENCES_LABEL_STYLES_INPUT")}
                    </span>
                </div>
                <div className='col-6 mt-3'>
                    <span className="d-block h6 lh-sm mb-0">
                        {translate("OTM_FORMS_TEMPLATE_PREFERENCES_LABEL_STYLES_NO_INPUT")}
                    </span>
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_BOLDED')}
                        name='preferences.itemLabelBolded'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_ITALIC')}
                        name='preferences.itemLabelItalic'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_UNDERLINE')}
                        name='preferences.itemLabelUnderline'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_BOLDED')}
                        name='preferences.itemTextBolded'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_ITALIC')}
                        name='preferences.itemTextItalic'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className="col-2">
                    <Checkbox
                        label={translate('OTM_LABEL_UNDERLINE')}
                        name='preferences.itemTextUnderline'
                        disabled={!allowUpdate}
                    />
                </div>
                <div className='col-6 mt-3'>
                    <Dropdown
                        buttonTooltip={true}
                        tooltip={translate("OTM_LABEL_FONT_SIZE_TOOLTIP")}
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_LABEL_FONT_SIZE')}
                        name="preferences.itemLabelFontSize"
                        readOnly={!allowUpdate}
                        options={LABEL_FONT_SIZE_OPTIONS}
                        onSelect={handleOnChange}
                        grow={true}
                    />
                </div>
                <div className='col-6 mt-3'>
                    <Dropdown
                        buttonTooltip={true}
                        tooltip={translate("OTM_LABEL_FONT_SIZE_TOOLTIP")}
                        heading={translate('OTM_FORMS_TEMPLATE_PREFERENCES_ITEM_TEXT_FONT_SIZE')}
                        name="preferences.itemTextFontSize"
                        readOnly={!allowUpdate}
                        options={LABEL_FONT_SIZE_OPTIONS}
                        onSelect={handleOnChange}
                        grow={true}
                    />
                </div>
            </div>
        );
    }, [dataTypes, LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS, allowUpdate, handleOnChange, translate]);

    const renderContent = useCallback((props) => {
        switch (selectedTab) {
            case "sections":
                return renderSections;
            case "preferences":
                return templatePreferences(props);
            default:
                return <div className="row mt-n3 align-items-start">
                    <div className='col-12 mt-3'>
                        <TextBox label={translate('OTM_FORM_TEMPLATE_NAME')} type='text' name='name' disabled={true} />
                    </div>
                    <div className='col-12 mt-3'>
                        <TextArea label={translate('OTM_DESCRIPTION')} type='text' name='description' disabled={!allowUpdate} />
                    </div>
                    <div className='col-12 col-lg-6 mt-3'>
                        <GenericCheckBox
                            tooltip={translate("OTM_FORM_TYPE_TOOLTIP")}
                            buttonTooltip={true}
                            initialValue={props.values.formType === 'banner'}
                            label={translate('OTM_FORM_TYPE')}
                            name='formType'
                            disabled={!allowUpdate}
                            onChange={(value) => { props.setFieldValue('formType', props.values['formType'] === 'banner' ? 'plain' : 'banner'); handleOnChange(); }}
                        />
                    </div>
                    <div className='col-12 col-lg-6 mt-3'>
                        <GenericCheckBox
                            tooltip={translate("OTM_SUBMIT_HIDDEN_FIELDS_TOOLTIP")}
                            buttonTooltip={true}
                            initialValue={props.values.submitHiddenFields}
                            label={translate('OTM_SUBMIT_HIDDEN_FIELDS')}
                            name='submitHiddenFields'
                            disabled={!allowUpdate}
                            onChange={(value) => { props.setFieldValue('submitHiddenFields', value); handleOnChange(); }}
                        />
                    </div>
                    <div className="col-12 col-lg-6 mt-3">
                        <Label
                            controlId={"templateGroupingId"}
                            label={translate("OTM_FORM_TEMPLATE_GROUPING_ID")}
                        />
                        <div className="row gx-2 align-items-center">
                            <div className="col">
                                <TextBox css="" type="text" name="templateGroupingId" disabled={true} readOnly={true} />
                            </div>
                            <div className="col-auto">
                                <ActionButton actionType="copy" valueToCopy={formTemplate.templateGroupingId} />
                            </div>
                        </div>
                    </div>
                </div>
        }
    }, [selectedTab, renderSections, templatePreferences, allowUpdate, translate, handleOnChange, formTemplate]);

    const renderModal = useMemo(() => {
        return <Formik initialValues={{
            name: formTemplate?.name || "",
            description: formTemplate?.description || "",
            formType: formTemplate?.formType || "",
            submitHiddenFields: formTemplate?.submitHiddenFields ?? false,
            templateGroupingId: formTemplate?.templateGroupingId || "",
            preferences: formTemplate?.preferences || {}
        }}
            validateOnMount
            validationSchema={Yup.object({
                name: Yup.string().matches(nameRegex, translate("OTM_INVALID_NAME")).required(translate("OTM_FORM_TEMPLATE_NAME_REQUIRED")),
                preferences: Yup.object({
                    itemMaxLength: Yup.number().integer(translate("OTM_MAX_LENGTH_MUST_BE_INTEGER")).nullable()
                })
            })}
            onSubmit={handleSubmit}
            enableReinitialize>
            {props => (<Form onChange={handleOnChange} className="flex-grow-1 d-flex flex-column">
                <Modal.Header>
                    {renderHeader(props)}
                </Modal.Header>

                <Modal.Body>
                    {renderContent(props)}
                </Modal.Body>

                <Modal.Footer>
                    {showSaveButton && allowUpdate && props.isValid && <Button size='sm' type="submit" variant='primary'>{translate('OTM_SAVE')}</Button>}
                    <Button size='sm' variant='secondary' onClick={onClose}>{translate(allowUpdate ? 'OTM_CANCEL' : 'OTM_CLOSE')}</Button>
                </Modal.Footer>
            </Form>)}
        </Formik>
    }, [formTemplate, showSaveButton, onClose, allowUpdate, renderHeader, renderContent, handleOnChange, handleSubmit, translate]);

    return <Modal
        container={modalContainerRef ? modalContainerRef.current : null}
        size={"lg"}
        show={show}
        backdrop={"static"}>
        {renderModal}
    </Modal>
}

export default EditFormTemplate;